import { reactive, ref } from "vue";
import {
  CancelSubscriptionFeedback,
  PackageInfo,
  Pricing,
  PricingQuery,
  SubscriptionDto,
  SubscriptionPurchaseRequest,
} from "@be/api";
import { api } from "@/backend";
import {
  contextAccount,
  isUserAllowedToCreateSubscriptions,
  isResellerChildAcctContext,
  isContextAcctReseller,
  isSuperReseller,
} from "@/state/account";
import { Code, ErrorCode } from "@/composables/useErrors";
import { execute as menuExecute, packages } from "@/state/menu";
import { useInfoBanner } from "@/composables/useInfoBanner";

const { addBanner } = useInfoBanner();

export type SubInfoTemp = SubscriptionDto & {
  tempId: number;
  plan: SubscriptionPlan;
  isNew: boolean;
  displayTotalPrice?: string | null;
  totalPrice?: number | null;
  displayUnitPrice?: string | null;
  unitPrice?: number | null;
  displayMonthlyPrice?: string | null;
  monthlyPrice?: number | null;
  isPricingLoading: boolean;
  modifiedNumberOfLicenses?: number;
  modifiedNumberOfMonths?: number;
  shouldConvertTrial?: boolean;
  shouldEndTrial?: boolean;
  requiresCallForPricing?: boolean;
  hasPricingLoaded: boolean;
};

export function hasModifications(sub: SubInfoTemp) {
  return (
    sub.requiresCallForPricing !== true &&
    (sub.isNew ||
      (sub.modifiedNumberOfLicenses && sub.modifiedNumberOfLicenses > 0) ||
      (sub.modifiedNumberOfMonths && sub.modifiedNumberOfMonths > 1))
  );
}

export const connectPreviewPackageId = 522;
export const connectPackageId = 644;
export const freePackages = [470, 573]; // missing children package IDs
export const bundlePackageId = 735;
export const excludedPackagesFromTrial = [...freePackages];
export const allowedNewPlanValues = ["single", "bundle"] as const;
export const subscriptionPlanValues = [
  ...allowedNewPlanValues,
  "trial",
] as const;

export type AllowedNewSubscriptionPlan = (typeof allowedNewPlanValues)[number];
export type SubscriptionPlan = (typeof subscriptionPlanValues)[number];

type SubscriptionTotal = {
  total?: number | null;
  totalDisplay?: string | null;
};

const subscriptionPlanNames: Record<SubscriptionPlan, string> = {
  trial: "Trial",
  single: "Single",
  bundle: "Bundle",
};

export function getPlanName(plan: SubscriptionPlan): string {
  return subscriptionPlanNames[plan] || "Unknown";
}

function mapSubscriptionToSubscriptionPricing(
  subscriptions?: SubInfoTemp[],
): PricingQuery[] {
  if (!subscriptions) return [];
  return subscriptions.map((s) => {
    return {
      licenseQty: s.modifiedNumberOfLicenses ?? s.numberOfLicenses,
      monthsQty: s.modifiedNumberOfMonths ?? s.numberOfMonths,
      packageId: s.packageId,
      subscriptionUuid: s.isNew ? undefined : s.subscriptionUuid,
      frontendRefId: s.tempId,
    };
  });
}

function updateSubscriptionsWithPricing(
  subscriptions: SubInfoTemp[],
  pricing?: Pricing[] | null,
) {
  if (!pricing) return [];

  pricing.forEach((p) => {
    var sub = subscriptions.find((s) =>
      p.subscriptionUuid
        ? s.subscriptionUuid === p.subscriptionUuid
        : s.tempId === p.frontendRefId,
    );

    if (sub) {
      if (p.unitPrice === null || p.unitPrice === undefined) {
        sub.requiresCallForPricing = true;
        sub.isPricingLoading = false;
        sub.hasPricingLoaded = true;
        return;
      }

      sub.totalPrice = p.totalPrice;
      sub.displayTotalPrice = p.displayTotalPrice;
      sub.unitPrice = p.unitPrice;
      sub.displayUnitPrice = p.displayUnitPrice;
      sub.monthlyPrice = p.monthlyPrice;
      sub.displayMonthlyPrice = p.displayMonthlyPrice;
      sub.requiresCallForPricing = false;
      sub.isPricingLoading = false;
      sub.hasPricingLoaded = true;
    }
  });
}

function mapSubscriptionToSubscriptionChanges(
  subscriptions: SubInfoTemp[],
): SubscriptionPurchaseRequest[] {
  return subscriptions.map((s) => {
    let subChanges = {} as SubscriptionPurchaseRequest;
    subChanges.subscriptionUuid = s.subscriptionUuid;
    subChanges.packageId = s.packageId;
    subChanges.numberOfPlayers =
      s.modifiedNumberOfLicenses ?? s.numberOfLicenses;
    subChanges.billRecurringly = s.billRecurringly;
    subChanges.po = s.po;
    subChanges.expireDate = s.expireDate;
    subChanges.numberOfMonths = s.modifiedNumberOfMonths ?? s.numberOfMonths;
    subChanges.isTrial = s.isTrial;
    subChanges.shouldConvertTrial = s.shouldConvertTrial ?? false;
    return subChanges;
  });
}

export const feedConfigReturnUrl = ref<string | undefined>(undefined);

let subscriptionStoreInstance: ReturnType<
  typeof createSubscriptionStore
> | null = null;

function createSubscriptionStore() {
  return reactive({
    subscribedPackageIds: undefined as number[] | undefined,
    subscriptions: undefined as SubInfoTemp[] | undefined,
    nextBillingDate: undefined as Date | undefined,
    unmodifiedTotal: undefined as SubscriptionTotal | undefined,
    modifiedTotal: undefined as SubscriptionTotal | undefined,
    proratedTotal: undefined as SubscriptionTotal | undefined,
    trialHistory: undefined as number[] | undefined,
    bundlePackage: undefined as PackageInfo | undefined,
    pendingSubscriptionsRequest: undefined as Promise<void> | undefined,
    pendingTrialDependenciesRequest: undefined as Promise<void> | undefined,
    openAddFeedModal: ref<boolean>(false),
    isLoadingSubscriptions: false as boolean,
    isLoadingPricing: false as boolean,
    offMenuPackages: undefined as PackageInfo[] | undefined,

    clear() {
      this.subscribedPackageIds = undefined;
      this.subscriptions = undefined;
      this.nextBillingDate = undefined;
      this.unmodifiedTotal = undefined;
      this.proratedTotal = undefined;
      this.modifiedTotal = undefined;
      this.trialHistory = undefined;
      this.pendingSubscriptionsRequest = undefined;
      this.pendingTrialDependenciesRequest = undefined;
    },

    async init() {
      if (this.subscriptions) return;

      await Promise.all([
        this.ensureTrialDependenciesLoaded(),
        this.fetchExpiringTrials(),
        menuExecute(),
        this.fetchOffMenuPackagesForAccount(),
      ]);
    },

    async ensureTrialDependenciesLoaded(): Promise<void> {
      if (this.pendingTrialDependenciesRequest)
        return this.pendingTrialDependenciesRequest;

      this.pendingTrialDependenciesRequest = Promise.all([
        this.fetchSubscriptions(false),
        this.fetchTrialHistory(),
        this.fetchBundlePackageInfo(),
      ])
        .then(() => undefined)
        .finally(() => {
          this.pendingTrialDependenciesRequest = undefined;
        });

      return this.pendingTrialDependenciesRequest;
    },

    getActiveSubscriptionsByPlan(plan: SubscriptionPlan) {
      return this.subscriptions?.filter(
        (sub) => sub.plan === plan && sub.isActive,
      ) as SubInfoTemp[] | undefined;
    },

    getActiveSubscriptionByPackageId(packageId: number) {
      return this.subscriptions?.find(
        (sub) => sub.packageId === packageId && sub.isActive,
      ) as SubInfoTemp | undefined;
    },

    getActiveNonTrialSubscriptionByPackageId(packageId: number) {
      return this.subscriptions?.find(
        (sub) => sub.packageId === packageId && sub.isActive && !sub.isTrial,
      ) as SubInfoTemp | undefined;
    },

    getActiveTrials() {
      return this.subscriptions?.filter(
        (sub) => sub.isTrial && sub.isActive && !sub.shouldEndTrial,
      ) as SubInfoTemp[] | undefined;
    },

    getActiveNonTrialSubscriptions(sort?: {
      orderBy: "packageName" | "expireDate";
      thenBy?: "packageName" | "expireDate";
      direction?: "asc" | "desc";
    }) {
      const activeNonTrialSubs = this.subscriptions?.filter(
        (sub) => !sub.isTrial && sub.isActive,
      ) as SubInfoTemp[] | undefined;

      if (!activeNonTrialSubs) return undefined;

      const withNewSubscriptionsFirst = (a: SubInfoTemp, b: SubInfoTemp) => {
        if (a.isNew === b.isNew) return 0;
        return a.isNew ? -1 : 1;
      };

      if (!sort)
        return activeNonTrialSubs.sort((a, b) =>
          withNewSubscriptionsFirst(a, b),
        );

      const sortByPackageName = (a: SubInfoTemp, b: SubInfoTemp) =>
        (a.packageName ?? "").localeCompare(b.packageName ?? "");

      const sortByExpireDate = (a: SubInfoTemp, b: SubInfoTemp) => {
        const aExpire = a.expireDate ? new Date(a.expireDate).getTime() : 0;
        const bExpire = b.expireDate ? new Date(b.expireDate).getTime() : 0;
        return aExpire - bExpire;
      };

      const compareBy = {
        packageName: sortByPackageName,
        expireDate: sortByExpireDate,
      };

      const primaryCompare = compareBy[sort.orderBy];
      const secondaryCompare =
        sort.thenBy && sort.thenBy !== sort.orderBy
          ? compareBy[sort.thenBy]
          : undefined;

      const direction = sort.direction === "desc" ? -1 : 1;

      return activeNonTrialSubs.sort((a, b) => {
        const isNewResult = withNewSubscriptionsFirst(a, b);
        if (isNewResult !== 0) return isNewResult;

        const primaryResult = primaryCompare(a, b) * direction;
        if (primaryResult !== 0 || !secondaryCompare) return primaryResult;
        return secondaryCompare(a, b) * direction;
      });
    },

    getExpiredTrials() {
      const now = new Date();
      const twoWeeksPrior = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 14,
      );
      return this.subscriptions?.filter((s) => {
        if (!s.isActive && s.expireDate && s.isTrial) {
          const expireDate = new Date(s.expireDate);
          return expireDate > twoWeeksPrior && expireDate <= now;
        }
        return false;
      });
    },

    getInactiveNonTrialSubscriptions() {
      return this.subscriptions?.filter(
        (sub) => !sub.isActive && !sub.isTrial,
      ) as SubInfoTemp[] | undefined;
    },

    async getSubscribedPackageIds() {
      if (!this.subscribedPackageIds) {
        await this.fetchSubscribedPackageIds();
      }
      return this.subscribedPackageIds ?? [];
    },

    async removeNewSubscription(tempId: number) {
      this.subscriptions = this.subscriptions?.filter(
        (s) => s.tempId !== tempId,
      );
      await this.fetchSubscriptionPricing(this.subscriptions, true);
    },

    resetModifiedSubscriptions() {
      this.subscriptions = this.subscriptions?.filter((s) => !s.isNew);
      this.subscriptions?.forEach((s) => {
        s.modifiedNumberOfLicenses = undefined;
        s.modifiedNumberOfMonths = undefined;
        s.shouldEndTrial = false;
        if (s.shouldConvertTrial) {
          s.isTrial = true;
          s.shouldConvertTrial = false;
          s.plan = "trial";
        }
      });
      this.subscriptions = this.subscriptions
        ? [...this.subscriptions]
        : undefined;
      this.proratedTotal = undefined;
      this.modifiedTotal = undefined;
    },

    calculateNewTotal() {
      const newTotal = this.getActiveNonTrialSubscriptions()
        ?.filter((s) => s.billRecurringly === true)
        ?.map((s) => s.totalPrice ?? 0)
        .reduce((prev, next) => (prev ?? 0) + (next ?? 0), 0);

      if (newTotal === undefined) return;

      this.modifiedTotal = {
        total: newTotal,
        totalDisplay:
          newTotal % 1 !== 0 ? newTotal.toFixed(2) : newTotal.toString(),
      };
    },

    findSubscriptionByTempId(tempId?: number) {
      return tempId
        ? this.subscriptions?.find((s) => s.tempId === tempId)
        : undefined;
    },

    hasChanges() {
      return !!this.subscriptions?.some(
        (s) =>
          s.requiresCallForPricing !== true &&
          (s.modifiedNumberOfLicenses !== undefined ||
            s.modifiedNumberOfMonths !== undefined ||
            s.isNew === true),
      );
    },

    isPricingLoaded() {
      return (
        this.getActiveNonTrialSubscriptions()?.every(
          (s) => s.hasPricingLoaded === true,
        ) ?? false
      );
    },

    hasBundlePlan() {
      return (this.getActiveSubscriptionsByPlan("bundle")?.length ?? 0) > 0;
    },

    hasActiveTrials() {
      return (this.getActiveTrials()?.length ?? 0) > 0;
    },

    hasOnlyFreePackages() {
      const activeSubs = this.getActiveNonTrialSubscriptions();
      if (!activeSubs || activeSubs.length === 0) return false;
      return activeSubs.every((s) => freePackages.includes(s.packageId!));
    },

    async hasTrialedPackage(packageId: number): Promise<boolean> {
      if (!this.trialHistory) await this.fetchTrialHistory();

      return this.trialHistory?.some((t) => t === packageId) ?? false;
    },

    async hasSubscriptionForPackage(packageId: number) {
      // lists bundled child packages if is bundle
      var activeSubPackages = await this.getSubscribedPackageIds();
      return activeSubPackages.some((p) => p === packageId);
    },

    isIncludedInBundlePlan(packageId: number) {
      return this.bundlePackage?.bundlePackageIds?.some((p) => p === packageId);
    },

    async checkActiveSubscriptionToPackage(
      packageId: number,
      includeTrials: boolean,
    ): Promise<{
      plan?: SubscriptionPlan;
      hasSubscription: boolean;
    }> {
      if (!this.subscriptions) await this.fetchSubscriptions(false);

      if (
        (this.getActiveSubscriptionsByPlan("bundle")?.length ?? 0) > 0 &&
        this.isIncludedInBundlePlan(packageId)
      )
        return {
          plan: "bundle",
          hasSubscription: true,
        };

      var sub = includeTrials
        ? this.getActiveSubscriptionByPackageId(packageId)
        : this.getActiveNonTrialSubscriptionByPackageId(packageId);
      if (sub)
        return {
          plan: sub.plan,
          hasSubscription: true,
        };

      return { hasSubscription: false };
    },

    canCreateTrials() {
      return this.getActiveSubscriptionsByPlan("bundle")?.length === 0;
    },

    canCheckout() {
      return (this.modifiedTotal?.total ?? 0) > 0 || this.hasOnlyFreePackages();
    },

    handleTrialConversion(
      trialSub: SubInfoTemp | undefined,
      packageId: number | undefined,
    ): SubInfoTemp | undefined {
      if (!trialSub) return;

      if (
        (isResellerChildAcctContext.value && isSuperReseller.value) ||
        (packageId !== undefined &&
          this.shouldCreateNewSubscription(trialSub.packageId!, packageId))
      ) {
        trialSub.shouldEndTrial = true;
        return undefined;
      } else {
        trialSub.isTrial = false;
        trialSub.isActive = true;
        trialSub.shouldConvertTrial = true;
        trialSub.modifiedNumberOfLicenses = 1;
        trialSub.modifiedNumberOfMonths = 1;
      }

      return trialSub;
    },

    shouldCreateNewSubscription(
      trialPackageId: number,
      targetPackageId: number,
    ): boolean {
      return (
        (trialPackageId === connectPreviewPackageId &&
          targetPackageId === connectPackageId) ||
        targetPackageId === bundlePackageId
      );
    },

    mapPackage(
      plan: AllowedNewSubscriptionPlan,
      packageId?: number,
    ): number | undefined {
      if (plan === "bundle") return bundlePackageId;

      if (packageId === connectPreviewPackageId) return connectPackageId;

      return packageId;
    },

    async renewSuperSubscription(tempSubId: number) {
      console.log("Renewing subscription with temp ID:", tempSubId);
      const sub = this.findSubscriptionByTempId(tempSubId);
      if (!sub)
        throw new Error("Unable to renew subscription; subscription not found");

      await this.addSubscription(
        sub.plan as AllowedNewSubscriptionPlan,
        sub.packageId,
        undefined,
        true,
        sub.billRecurringly,
        sub.numberOfLicenses ?? 1,
        sub.numberOfMonths ?? 1,
      );
    },

    async addSubscription(
      plan: AllowedNewSubscriptionPlan,
      packageId?: number,
      trialSubTempId?: number,
      overrideExistingPlanCheck = false,
      billRecurringly: boolean = true,
      numberOfLicenses: number = 1,
      numberOfMonths: number = 1,
    ): Promise<{ isSuccess: boolean; error?: Code }> {
      if (!this.subscriptions) this.subscriptions = [];

      let sub = this.findSubscriptionByTempId(trialSubTempId);
      packageId = this.mapPackage(plan, sub?.packageId ?? packageId);

      if (plan === "single" && !packageId && !trialSubTempId) {
        throw new Error(
          "Package ID must be provided when adding a single plan subscription",
        );
      }

      const checkRes = await this.checkActiveSubscriptionToPackage(
        packageId!,
        false,
      );
      if (
        plan === "single" &&
        checkRes.plan === "bundle" &&
        checkRes.hasSubscription &&
        !overrideExistingPlanCheck
      ) {
        return { isSuccess: false, error: ErrorCode.HasBundlePlan };
      } else if (checkRes.hasSubscription && !overrideExistingPlanCheck) {
        return { isSuccess: false, error: ErrorCode.AlreadySubscribed };
      }

      sub = this.handleTrialConversion(sub, packageId);

      if (!sub) {
        let pkg = packageId
          ? [
              ...(this.bundlePackage ? [this.bundlePackage] : []),
              ...(this.offMenuPackages ?? []),
              ...packages.value,
            ].find((p) => p?.packageId === packageId)
          : undefined;

        if (!pkg) pkg = await this.getPackageById(packageId);

        if (!pkg) {
          console.error("AddSubscription: Package not found", packageId);
          return { isSuccess: false, error: ErrorCode.PackageNotFound };
        }

        sub = {
          tempId: Math.floor(Math.random() * 1000000000),
          plan,
          isNew: true,
          isActive: true,
          isPricingLoading: true,
          isTrial: false,
          numberOfLicenses: numberOfLicenses,
          numberOfMonths: numberOfMonths,
          modifiedNumberOfLicenses: numberOfLicenses,
          modifiedNumberOfMonths: numberOfMonths,
          packageThumbnail: pkg?.thumbnail,
          packageName: pkg?.name,
          packageId: packageId,
          billRecurringly: billRecurringly,
          hasPricingLoaded: false,
        } as SubInfoTemp;

        this.subscriptions.unshift(sub);
      }

      await this.fetchSubscriptionPricing([sub], false);

      return { isSuccess: true };
    },

    async tryCreateTrial(packageId: number): Promise<{
      hasSubscription: boolean;
      isTrialCreated: boolean;
      error?: Code;
    }> {
      await this.ensureTrialDependenciesLoaded();

      const sub = await this.checkActiveSubscriptionToPackage(packageId, true);
      if (sub.hasSubscription)
        return { hasSubscription: true, isTrialCreated: false };

      const hasTrialed = await this.hasTrialedPackage(packageId);
      if (hasTrialed)
        return {
          hasSubscription: false,
          isTrialCreated: false,
          error: ErrorCode.NeedsSubscription,
        };

      try {
        await this.createTrial(packageId);
      } catch (err) {
        console.error("Error creating trial", err);
        return {
          hasSubscription: false,
          isTrialCreated: false,
          error: ErrorCode.ErrorCreatingTrial,
        };
      }

      return { hasSubscription: true, isTrialCreated: true };
    },

    async modifySubscription(modifiedSub: {
      tempId: number;
      numLicense?: number;
      numMonths?: number;
    }) {
      if (!this.subscriptions) return;

      const ogSub = this.findSubscriptionByTempId(modifiedSub.tempId);
      if (ogSub) {
        if (modifiedSub.numLicense) {
          ogSub.modifiedNumberOfLicenses =
            ogSub.numberOfLicenses === modifiedSub.numLicense
              ? ogSub.isNew
                ? modifiedSub.numLicense
                : undefined
              : modifiedSub.numLicense;
        }

        if (modifiedSub.numMonths) {
          ogSub.modifiedNumberOfMonths =
            ogSub.numberOfMonths === modifiedSub.numMonths
              ? ogSub.isNew
                ? modifiedSub.numMonths
                : undefined
              : modifiedSub.numMonths;
        }

        await this.fetchSubscriptionPricing([ogSub], false);
      }
    },

    async archiveSubscription(tempId: number) {
      const sub = this.findSubscriptionByTempId(tempId);
      if (sub) sub.isActive = false;
    },

    async fetchSubscribedPackageIds(force: boolean = false): Promise<void> {
      if (this.subscribedPackageIds && !force) return;

      return api
        .getSubscribedPackages({ accountId: contextAccount.value?.accountId })
        .then((response) => {
          this.subscribedPackageIds = response.data.data?.packageIds ?? [];
        });
    },

    async fetchSubscriptions(force: boolean = false): Promise<void> {
      if (
        (!force && this.subscriptions) ||
        !isUserAllowedToCreateSubscriptions.value
      )
        return Promise.resolve();

      if (this.pendingSubscriptionsRequest)
        return this.pendingSubscriptionsRequest;

      this.isLoadingSubscriptions = true;
      this.pendingSubscriptionsRequest = api
        .getSubscriptions({ accountId: contextAccount.value?.accountId })
        .then((response) => {
          const res = response.data.data;
          const subs = res?.subscriptions || [];

          if (res?.nextBillingDate)
            this.nextBillingDate = new Date(res.nextBillingDate);

          this.subscriptions = subs.map((sub) => {
            let plan: SubscriptionPlan;
            if (sub.isTrial) {
              plan = "trial";
            } else if (
              sub.packageId &&
              bundlePackageId === sub.packageId &&
              sub.isActive
            ) {
              plan = "bundle";
            } else {
              plan = "single";
            }

            return {
              ...sub,
              tempId: Math.floor(Math.random() * 1000000000),
              plan,
              isNew: false,
              hasPricingLoaded: false,
            } as SubInfoTemp;
          });
        })
        .finally(() => {
          this.isLoadingSubscriptions = false;
          this.pendingSubscriptionsRequest = undefined;
        });

      return this.pendingSubscriptionsRequest;
    },

    async fetchSubscriptionPricing(
      subscriptions?: SubInfoTemp[],
      shouldLoadAll: boolean = false,
    ): Promise<void> {
      const pricingLoaded = this.isPricingLoaded();
      if (pricingLoaded && shouldLoadAll) return;

      this.isLoadingPricing = shouldLoadAll;

      const pricingReqs = mapSubscriptionToSubscriptionPricing(subscriptions);
      return api
        .getSubscriptionPricing({
          accountId: contextAccount.value?.accountId,
          includeAll: shouldLoadAll,
          pricingRequests: pricingReqs,
        })
        .then((res) => {
          const pricing = res.data.data;
          if (pricing && shouldLoadAll) {
            this.unmodifiedTotal = {
              total: pricing.persistedTotal,
              totalDisplay: pricing.displayPersistedTotal,
            };
          }
          if (this.subscriptions) {
            updateSubscriptionsWithPricing(
              this.subscriptions,
              res.data.data?.pricingResults,
            );
            this.calculateNewTotal();
          }
        })
        .finally(() => {
          this.isLoadingPricing = false;
        });
    },

    async fetchProratedTotal(): Promise<string | undefined> {
      const modifiedSubs = this.subscriptions?.filter(
        (s) => s.modifiedNumberOfLicenses || s.modifiedNumberOfMonths,
      );

      if (!modifiedSubs || modifiedSubs.length === 0)
        return Promise.resolve(undefined);

      const pricingReqs = mapSubscriptionToSubscriptionPricing(modifiedSubs);
      return api
        .getProratedTotal({
          accountId: contextAccount.value?.accountId,
          changes: pricingReqs,
        })
        .then((res) => {
          const total = res.data.data?.displayCost;
          if (!total)
            throw new Error("Failed to fetch subscription changes total");

          return total;
        });
    },

    async createTrial(packageId: number): Promise<void> {
      return api
        .createTrialSubscription({
          packageId: packageId,
          accountId: contextAccount.value?.accountId,
        })
        .then(async (res) => {
          if (res.data.data) {
            this.subscriptions ??= [];
            this.subscriptions.push({
              ...res.data.data,
              tempId: Math.floor(Math.random() * 1000000000),
              plan: "trial",
              isNew: false,
              isPricingLoading: false,
              hasPricingLoaded: true,
            });

            if (!this.trialHistory) this.trialHistory = [];
            this.trialHistory.push(packageId);

            await this.fetchSubscribedPackageIds(true);
          }
        });
    },

    async purchaseSubscriptions(): Promise<void> {
      try {
        await api.purchaseSubscriptions({
          accountId: contextAccount.value?.accountId,
          changes: mapSubscriptionToSubscriptionChanges(
            this.subscriptions?.filter((s) => hasModifications(s)) || [],
          ),
        });

        const convertedTrialsToBundlePlan =
          this.subscriptions?.filter((s) => s.shouldEndTrial === true) ?? [];

        if (convertedTrialsToBundlePlan.length > 0) {
          for (const sub of convertedTrialsToBundlePlan) {
            await this.cancelSubscription(sub.tempId, [
              {
                reason: "Converted trial to Everything",
              },
            ]);
          }
        }

        await this.fetchSubscriptions(true);
        await this.fetchSubscribedPackageIds(true);
        await this.fetchSubscriptionPricing(undefined, true);
      } catch (error: any) {
        console.error("Error purchasing subscriptions", error);
        throw new Error(
          error?.response?.data?.error?.message ||
            "Error purchasing subscriptions",
        );
      }
    },

    async fetchTrialHistory(): Promise<void> {
      if (this.trialHistory || !isUserAllowedToCreateSubscriptions.value)
        return;

      return api
        .getTrialHistory({ accountId: contextAccount.value?.accountId })
        .then((res) => {
          if (res.data.data)
            this.trialHistory = res.data.data?.packageIds ?? [];
        });
    },

    async fetchExpiringTrials(): Promise<void> {
      if (
        !isUserAllowedToCreateSubscriptions.value ||
        isResellerChildAcctContext.value ||
        isContextAcctReseller.value
      )
        return;

      return api
        .getExpiringTrials({ accountId: contextAccount.value?.accountId })
        .then((res) => {
          if (res.data.data?.trials && res.data.data.trials.length > 0) {
            const expiringTrials = res.data.data.trials;
            const allExpired = expiringTrials.every((t) => t.isExpired);

            const message = allExpired
              ? `You have trials that have recently expired!`
              : `You have trials expiring soon!`;
            addBanner({
              key: "expiring-trials",
              message: message,
              link: "/subscribe",
              dismissForDays: 14,
              linkTarget: "_self",
              linkText: "Click here to manage subscriptions",
            });
          }
        });
    },

    async fetchBundlePackageInfo(): Promise<void> {
      if (this.bundlePackage) return;

      return api
        .getPackagesByIds({ packageIds: [bundlePackageId] })
        .then((res) => {
          if (res.data.data && res.data.data.length > 0) {
            this.bundlePackage = res.data.data[0];
          }
        });
    },

    async getPackageById(packageId?: number): Promise<PackageInfo | undefined> {
      if (!packageId) return undefined;

      return api.getPackagesByIds({ packageIds: [packageId] }).then((res) => {
        if (res.data.data && res.data.data.length > 0) {
          return res.data.data[0];
        }
        return undefined;
      });
    },

    async reactivateSubscription(tempSubId: number): Promise<void> {
      const sub = this.findSubscriptionByTempId(tempSubId);
      if (!sub)
        throw new Error(
          "Unable to cancel subscription; subscription not found",
        );

      return api
        .reactivateSubscription({
          accountId: contextAccount.value?.accountId,
          subscriptionUuid: sub.subscriptionUuid,
          quantity: (sub.numberOfLicenses ?? 1) <= 0 ? 1 : sub.numberOfLicenses,
        })
        .then(async () => {
          sub.isActive = true;
          sub.numberOfLicenses =
            (sub.numberOfLicenses ?? 1) <= 0 ? 1 : sub.numberOfLicenses;
          await this.fetchSubscriptionPricing(undefined, true);
          await this.fetchSubscribedPackageIds(true);
        })
        .catch((error) => {
          console.error("Error reactivating subscription", error);
          throw error;
        });
    },

    async cancelSubscription(
      tempSubId: number,
      feedback: CancelSubscriptionFeedback[],
    ): Promise<void> {
      const sub = this.findSubscriptionByTempId(tempSubId);
      if (!sub)
        throw new Error(
          "Unable to cancel subscription; subscription not found",
        );

      return api
        .cancelSubscription({
          accountId: contextAccount.value?.accountId,
          subscriptionUuid: sub.subscriptionUuid,
          feedback: feedback,
        })
        .then(async () => {
          this.archiveSubscription(tempSubId);
          await this.fetchSubscriptionPricing(undefined, true);
          await this.fetchSubscribedPackageIds(true);
        })
        .catch((error) => {
          console.error("Error cancelling subscription", error);
          throw error;
        });
    },

    async setRefererrer(referrerId: string): Promise<void> {
      await api
        .setReferrer({
          referrer: referrerId,
        })
        .catch((error) => {
          console.error("Error setting referrer", error);
          throw error;
        });
    },

    async fetchOffMenuPackagesForAccount(): Promise<void> {
      if (!contextAccount.value?.accountId) return Promise.resolve();

      return api
        .getOffMenuAccountPackages({
          accountId: contextAccount.value?.accountId,
        })
        .then((res) => {
          this.offMenuPackages = res.data.data ?? undefined;
          return;
        })
        .catch((error) => {
          console.error("Error fetching off-menu packages", error);
          throw error;
        });
    },
  });
}

export const subscriptionStore = (() => {
  if (!subscriptionStoreInstance) {
    subscriptionStoreInstance = createSubscriptionStore();
  }
  return subscriptionStoreInstance;
})();
